<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { normalizeString } from "@/modules/companyNeed/helpers/move";

import type { WorkerBulkAction, WorkersBulkActionsModalProps } from "@/modules/worker/types/bulkActions";

import Modal from "@/dsComponents/modals/Modal.vue";
import QuickActionBadge from "@/dsComponents/QuickActions/QuickActionBadge.vue";
import QuickActionItem from "@/dsComponents/QuickActions/QuickActionItem.vue";
import QuickActionSearch from "@/dsComponents/QuickActions/QuickActionSearch.vue";
import QuickActionsSeparator from "@/dsComponents/QuickActions/QuickActionsSeparator.vue";
import { useQuickActionsNavigation } from "@/dsComponents/QuickActions/useQuickActionsNavigation";

const props = defineProps<WorkersBulkActionsModalProps>();

const emit = defineEmits<{
    close: [];
}>();

const { t } = useI18n();

const search = ref("");

const filteredActions = computed(() => {
    if (!search.value) return props.actions;
    const query = normalizeString(search.value);
    return props.actions.filter((action) => normalizeString(action.label).includes(query));
});

const defaultActions = computed(() => filteredActions.value.filter((action) => !action.danger));
const dangerActions = computed(() => filteredActions.value.filter((action) => action.danger));
const orderedActions = computed(() => [...defaultActions.value, ...dangerActions.value]);

let hasExecuted = false;

const handleExecute = (action: WorkerBulkAction | undefined) => {
    if (!action || hasExecuted) return;
    hasExecuted = true;
    emit("close");
    action.onClick();
};

const { selectedItemIndex, handleKeyDown, updateItemCount } = useQuickActionsNavigation({
    onExecute: (index: number) => handleExecute(orderedActions.value[index]),
});

watch(
    () => orderedActions.value.length,
    (count) => updateItemCount(count),
    { immediate: true }
);
</script>

<template>
    <Modal
        class="workers-bulk-actions-modal"
        position="top"
        width="720px"
        :open="open"
        no-padding
        no-padding-header
        @close="emit('close')"
    >
        <template #header>
            <div class="workers-bulk-actions-modal__header">
                <QuickActionBadge :label="label" />
                <QuickActionSearch
                    v-model="search"
                    :is-focused="open"
                    :placeholder="t('page.profile.bulkActions.placeholder')"
                    @clear="search = ''"
                    @keydown="handleKeyDown"
                />
            </div>
        </template>
        <template #default>
            <div class="workers-bulk-actions-modal__content">
                <QuickActionItem
                    v-for="(action, index) in defaultActions"
                    :key="action.key"
                    :selected="selectedItemIndex === index"
                    @click="handleExecute(action)"
                >
                    <template #infos>
                        <div class="workers-bulk-actions-modal__item">
                            <i :class="['ti', `ti-${action.icon}`, 'typo-md-regular']" />
                            <span class="typo-xs-medium">{{ action.label }}</span>
                        </div>
                    </template>
                </QuickActionItem>
                <QuickActionsSeparator v-if="defaultActions.length && dangerActions.length" />
                <QuickActionItem
                    v-for="(action, index) in dangerActions"
                    :key="action.key"
                    :selected="selectedItemIndex === index + defaultActions.length"
                    @click="handleExecute(action)"
                >
                    <template #infos>
                        <div class="workers-bulk-actions-modal__item workers-bulk-actions-modal__item--danger">
                            <i :class="['ti', `ti-${action.icon}`, 'typo-md-regular']" />
                            <span class="typo-xs-medium">{{ action.label }}</span>
                        </div>
                    </template>
                </QuickActionItem>
            </div>
        </template>
    </Modal>
</template>

<style scoped lang="scss">
.workers-bulk-actions-modal {
    &__header {
        padding: var(--spacing-md, 8px) var(--spacing-lg, 12px);
        display: flex;
        flex-direction: column;
        align-items: start;
        width: 100%;
    }

    &__content {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: var(--spacing-xxs, 2px);
        padding: var(--spacing-xs, 4px) var(--spacing-lg, 12px) var(--spacing-md, 8px) var(--spacing-md, 8px);
    }

    &__item {
        display: flex;
        align-items: center;
        gap: var(--spacing-lg, 12px);
        user-select: none;
        color: var(--text-sub-600, #494e61);

        &--danger {
            color: var(--red-base, #e51940);
        }
    }
}

.search-result-item:hover,
.search-result-item--selected {
    .workers-bulk-actions-modal__item:not(.workers-bulk-actions-modal__item--danger) {
        color: var(--text-main-900, #00061a);
    }
}
</style>
